import { useEffect, useState } from "react";
import { storage } from "../../state/storage.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";
import { applyDisplayRegex, createScript, type RegexPlacement, type Script } from "@roqeet/core";

/**
 * 정규식 스크립트 관리(결정 #21 렌더 파이프라인의 정규식 단계 / #17 safe 등급).
 *
 * 정규식 스크립트는 메시지 표시/입력/출력 단계에서 텍스트를 찾아 치환한다. 여기서
 * 만든 스크립트를 캐릭터 에디터에서 캐릭터에 연결하면(scriptIds), 대화 렌더 시
 * 자동 적용된다(ChatRoomView render 파이프라인).
 *
 * JS/Lua 트리거(샌드박스 필요, #4)는 후속 과제 — 여기서는 런타임이 필요 없는
 * "regex" 언어만 다룬다.
 */
const PLACEMENT_KEYS: Record<RegexPlacement, TKey> = {
  "user-input": "rx.placeUserInput",
  "ai-output": "rx.placeAiOutput",
  "slash-command": "rx.placeSlash",
  "world-info": "rx.placeWorldInfo",
  "display-only": "rx.placeDisplayOnly",
};

export function ScriptsSection() {
  const { t } = useT();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [editing, setEditing] = useState<Script | "new" | null>(null);

  const reload = async () => {
    const all = await storage.scripts.list();
    setScripts(all.filter((s) => s.language === "regex"));
  };
  useEffect(() => {
    void reload();
  }, []);

  const remove = async (s: Script) => {
    if (!confirm(t("rx.deleteConfirm", { name: s.name }))) return;
    await storage.scripts.delete(s.id);
    await reload();
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("rx.title")}
      </h2>
      <div className="space-y-2">
        {scripts.length === 0 && !editing && (
          <p className="rounded-xl border border-line bg-bg-soft px-3 py-3 text-xs text-ink-faint">
            {t("rx.empty")}
          </p>
        )}

        <ul className="space-y-2">
          {scripts.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-line bg-bg-soft px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <span className="truncate text-sm text-ink">{s.name}</span>
                  {!s.enabled && (
                    <span className="rounded bg-bg-raised px-1.5 py-0.5 text-xs text-ink-faint">
                      {t("common.disabledBadge")}
                    </span>
                  )}
                </div>
                <div className="truncate font-mono text-xs text-ink-faint">/{s.source}/</div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                  onClick={() => setEditing(s)}
                >
                  {t("common.edit")}
                </button>
                <button
                  className="rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised"
                  onClick={() => remove(s)}
                >
                  {t("common.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>

        {editing ? (
          <ScriptForm
            script={editing === "new" ? undefined : editing}
            onDone={async () => {
              setEditing(null);
              await reload();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <button
            className="w-full rounded-xl border border-dashed border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => setEditing("new")}
          >
            {t("rx.add")}
          </button>
        )}
      </div>
    </section>
  );
}

function ScriptForm({
  script,
  onDone,
  onCancel,
}: {
  script?: Script;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [name, setName] = useState(script?.name ?? "");
  const [source, setSource] = useState(script?.source ?? "");
  const [flags, setFlags] = useState(script?.regex?.flags ?? "g");
  const [replacement, setReplacement] = useState(script?.regex?.replacement ?? "");
  const [placement, setPlacement] = useState<RegexPlacement[]>(
    script?.regex?.placement ?? ["display-only"],
  );
  const [enabled, setEnabled] = useState(script?.enabled ?? true);
  const [testInput, setTestInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const togglePlacement = (p: RegexPlacement) => {
    setPlacement((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  };

  // 실시간 미리보기(결정 #21 정규식 단계와 동일한 함수 사용).
  const preview = (() => {
    if (!testInput || !source) return "";
    try {
      const probe: Script = {
        id: "preview" as Script["id"],
        sync: { rev: 0, createdAt: 0, updatedAt: 0, deletedAt: null },
        name: "preview",
        language: "regex",
        enabled: true,
        source,
        regex: { flags, replacement, placement: ["display-only"] },
      };
      return applyDisplayRegex(testInput, [probe]);
    } catch {
      return t("rx.regexError");
    }
  })();

  const save = async () => {
    if (!name.trim() || !source.trim()) {
      setErr(t("rx.errNamePattern"));
      return;
    }
    if (placement.length === 0) {
      setErr(t("rx.errPlacement"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const regex = { flags, replacement, placement };
      if (script) {
        await storage.scripts.put({ ...script, name: name.trim(), enabled, source: source.trim(), regex });
      } else {
        const s = createScript({
          name: name.trim(),
          language: "regex",
          source: source.trim(),
          enabled,
          regex,
        });
        await storage.scripts.put(s);
      }
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
      <input
        className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
        placeholder={t("rx.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-line bg-bg-raised px-2 py-1.5 font-mono text-sm text-ink outline-none focus:border-accent"
          placeholder={t("rx.patternPlaceholder")}
          value={source}
          onChange={(e) => setSource(e.target.value)}
        />
        <input
          className="w-16 rounded-lg border border-line bg-bg-raised px-2 py-1.5 font-mono text-sm text-ink outline-none focus:border-accent"
          placeholder={t("rx.flagsPlaceholder")}
          value={flags}
          onChange={(e) => setFlags(e.target.value)}
          title={t("rx.flagsTitle")}
        />
      </div>
      <input
        className="w-full rounded-lg border border-line bg-bg-raised px-2 py-1.5 font-mono text-sm text-ink outline-none focus:border-accent"
        placeholder={t("rx.replacementPlaceholder")}
        value={replacement}
        onChange={(e) => setReplacement(e.target.value)}
      />

      <div className="flex flex-wrap gap-2 text-xs">
        {(Object.keys(PLACEMENT_KEYS) as RegexPlacement[]).map((p) => (
          <label key={p} className="flex items-center gap-1 text-ink-soft">
            <input
              type="checkbox"
              checked={placement.includes(p)}
              onChange={() => togglePlacement(p)}
            />
            {t(PLACEMENT_KEYS[p])}
          </label>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {t("common.enabled")}
      </label>

      {/* 미리보기 */}
      <div className="rounded-lg border border-line bg-bg-raised p-2">
        <input
          className="w-full bg-transparent text-sm text-ink outline-none"
          placeholder={t("rx.testPlaceholder")}
          value={testInput}
          onChange={(e) => setTestInput(e.target.value)}
        />
        {testInput && (
          <div className="mt-1 border-t border-line pt-1 font-mono text-xs text-ink-faint">
            → {preview}
          </div>
        )}
      </div>

      {err && <p className="text-xs text-red-300">{err}</p>}

      <div className="flex justify-end gap-2">
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={onCancel}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={save}
          disabled={busy}
        >
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
